import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  Button,
  EmptyState,
  Icon,
  Panel,
  Stat,
  Tabs,
  Tag,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatCount, formatPesos } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { ResidenceValue } from '../../lib/residence.tsx';
import { useSession } from '../../lib/session.tsx';
import { NoteDialog } from '../players/dialogs/NoteDialog.tsx';
import { TestAccountDialog } from '../players/dialogs/TestAccountDialog.tsx';
import { RestrictDialog } from '../players/dialogs/RestrictDialog.tsx';
import { StatusDialog } from '../players/dialogs/StatusDialog.tsx';
import { TierDialog } from '../players/dialogs/TierDialog.tsx';
import { STATUS_MOVES, type StatusMoveTarget } from '../players/vocabulary.ts';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { LiftDialog } from './dialogs/LiftDialog.tsx';
import { restrictionColumns } from './controls.tsx';
import { CLOCK, formatDay, formatManilaMoment, initials } from './format.ts';
import { ActivityTab } from './tabs/ActivityTab.tsx';
import { BalancesTab } from './tabs/BalancesTab.tsx';
import { BonusesTab } from './tabs/BonusesTab.tsx';
import { KycTab } from './tabs/KycTab.tsx';
import { PointsTab } from './tabs/PointsTab.tsx';
import { RestrictionsTab } from './tabs/RestrictionsTab.tsx';
import { SecurityTab, signInStanding, type SecurityView } from './tabs/SecurityTab.tsx';
import {
  CredentialActionDialog,
  type CredentialAction,
} from './dialogs/CredentialActionDialog.tsx';
import { LogsTab } from './tabs/LogsTab.tsx';
import { TransactionsTab } from './tabs/TransactionsTab.tsx';
import {
  CONTROLS,
  KYC_TONE,
  STATUS_TONE,
  TABS,
  TIER_LABEL,
  isTabKey,
  riskBand,
  sentence,
  type TabKey,
} from './vocabulary.ts';
import type { ControlRow, PlayerDetail } from './types.ts';

/**
 * Why the one disabled button in the title block is disabled, attached to it
 * with aria-describedby rather than left as loose text — a screen reader
 * announcing "Adjust balance, dimmed" with nothing attached tells somebody the
 * control is broken, when in fact it deliberately lives on another screen.
 *
 * It says "the two" no longer: Message stopped being disabled when it became a
 * link to the support composer, so there is one inert control left.
 *
 * AND IT NO LONGER READS AN ENDPOINT OUT LOUD. The sentence used to name the
 * route an adjustment is raised on — `POST /api/adjustments` — to whoever was
 * listening with a screen reader, which is the one audience least able to do
 * anything with it. The route belongs here, in the source, where somebody
 * wiring the form will look for it. What the operator needs from this sentence
 * is that the platform CAN do it, that it takes a second person, and which
 * screen to go to; all three survive.
 */
const INERT_HERE =
  'Adjust balance is disabled on this profile: the platform does record balance adjustments, but every one of them needs a second person to approve it, so the form and the queue that approves it both sit on Player funds.';

/**
 * The player profile — the full-page record behind every row in the players
 * list, at /players/:id.
 *
 * ONE read backs the page itself:
 *   GET /api/players/:id → { player, restrictions, notes, kycSubmissions,
 *                             bonusGrants, balances }
 *
 * The last two are new, and they closed two tabs that were declining to show
 * data this platform holds. Balances said adjustments were "not read on this
 * screen" and Bonuses said grants were "not on this route": both were true of
 * the route and neither was true of the database, which holds
 * `wallet.manual_adjustment` and `promo.bonus_grant` rows for these players.
 * `balances.adjustments` and `balances.withdrawable` are NULL rather than
 * absent when the session lacks `transactions.view` — the gate the ledger, the
 * withdrawal queue and the adjustment queue already share — so a Support Agent
 * still opens the profile and reads a sentence naming the key instead of a 403.
 *
 * Two tabs make a second read of their own, because both are PAGED and neither
 * belongs in a payload the whole page waits on:
 *   GET /api/transactions?playerId=…   transactions.view — the ledger
 *   GET /api/points/players/:id        players.view — the points ledger
 *
 * FIVE decisions are live from this screen, each landing its row and its audit
 * entry in one transaction:
 *
 *   POST /api/players/restrictions            players.restrict
 *   POST /api/players/restrictions/:id/lift   players.restrict
 *   POST /api/players/status                  players.restrict — suspend, flag,
 *                          reinstate. Only the moves the account's current
 *                          status permits are offered, which is the same rule
 *                          the route enforces.
 *   POST /api/players/tier                    players.tier
 *   POST /api/players/:id/notes               players.note
 *
 * The KYC verdict was a sixth and seventh — approve and reject on an older
 * submission — until the owner closed that process on 23 October 2026. The KYC
 * tab lists those submissions read-only now; a player's identity is decided on
 * the identity queue.
 *
 * TWO are still inert, for two different reasons, both named beside them:
 * Message has an outbox to write to — platform.mail_message — and nothing
 * that decides on a send to one chosen player: no route, no players.message
 * capability, and no template for operator-written copy, the registry being
 * closed by a CHECK on mail_message.template. Adjust balance HAS a route —
 * POST /api/adjustments — but an adjustment needs a second approver and both
 * the form and that checker queue live on Player funds.
 *
 * WHAT STILL HAS NO SOURCE, and is therefore named rather than drawn: game
 * rounds, the individual rounds behind a grant's wagering total
 * (`promo.bonus_wagering`, which no route reads), and the document files of an
 * older KYC submission, which were never uploaded. Each says why, on the tab it
 * belongs to or in the panel at the foot of the page.
 *
 * FOUR THINGS LEFT THIS LIST, because they turned out to have a source all
 * along: the bonus grants and their wagering state, the locked-bonus balance
 * (`wallet.account` kind PLAYER_BONUS, summed from the same ledger the cash
 * balance is summed from), the points balance, and withdrawable — which the
 * player's own wallet route has been computing as `cash − open requests` the
 * whole time. The old notice defined it as "cash minus locked bonus", and that
 * was wrong: bonus money lives in a separate account and was never inside the
 * cash figure to subtract.
 */

export function PlayerProfile() {
  const { id: playerId = '' } = useParams<{ id: string }>();
  const { brandId, brands, allowed, session } = useSession();
  /**
   * The capability this module needs, read once and used in BOTH places that
   * need it: the refusal that decides what is painted, and the effects that
   * decide what is fetched. An early return covers only the first.
   */
  const canReadPlayers = allowed('players.view');
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [detail, setDetail] = useState<PlayerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  /*
   * Whether the ONE read behind this page failed, as against answering that no
   * such player is visible. Those are different facts and the page says so
   * below: "This player did not load" versus "No such player".
   *
   * It also guards the only real-zero claim on the screen. The internal-notes
   * panel states that player.player_note has no rows for this player, and that
   * sentence is only true because a failed read never reaches it — the record
   * is cleared in the catch, so the whole page falls to the not-loaded state
   * first. Keep that early return ahead of the tabs.
   */
  const [failed, setFailed] = useState(false);
  /** When the balance was actually read. The tile says so instead of "as of now". */
  const [readAt, setReadAt] = useState<Date | null>(null);

  /* ── Writes ──────────────────────────────────────────────────────────────
     Bumped after every decision so the ONE read behind this page runs again.
     Nothing is patched in place: applying a control moves the status pill, the
     Active-restrictions tile and the roster this profile was opened from. */
  const [writeCount, setWriteCount] = useState(0);

  type DialogKey =
    | 'restrict'
    | 'lift'
    | 'note'
    | 'tier'
    | 'test-account'
    | 'sign-out'
    | 'force-reset'
    | 'resend-verification'
    | StatusMoveTarget;
  const [dialog, setDialog] = useState<DialogKey | null>(null);

  /*
   * The security tab's own read, kept out of the profile's main load.
   *
   * It is a SECOND request and it fires only when the tab is opened, because
   * `GET /api/players/:id/sessions` returns up to fifty rows that nobody on the
   * other seven tabs is looking at. It also re-reads after every credential
   * action, which is the point: the count in the dialog's warning and the list
   * behind it both have to move when a session ends.
   */
  const [security, setSecurity] = useState<SecurityView | null>(null);
  const [securityFailed, setSecurityFailed] = useState(false);

  const [liftTarget, setLiftTarget] = useState<ControlRow | null>(null);
  const [lifting, setLifting] = useState<string | null>(null);

  // An unknown ?tab= falls back to Activity rather than rendering nothing — a
  // stale link in a ticket should still open the profile.
  const raw = searchParams.get('tab') ?? 'activity';
  const tab: TabKey = isTabKey(raw) ? raw : 'activity';

  const setTab = (key: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    // Replace, not push: the seven tabs are views of one record, so Back should
    // return to the players list rather than walk backwards through tabs.
    setSearchParams(next, { replace: true });
  };

  /*
   * The security tab's read, and the re-read after every credential action.
   *
   * Guarded on `canReadPlayers` for the reason the profile's own load states
   * below: a refusal is a RENDER decision that runs after every hook, so an
   * unguarded effect still fires for a role the route refuses — and the 403
   * raises a modal claiming a failure on top of the notice explaining it.
   */
  const loadSecurity = useCallback(async () => {
    if (!canReadPlayers || !playerId) return;
    try {
      const data = await api.get<SecurityView>(
        `/api/players/${encodeURIComponent(playerId)}/sessions`,
      );
      setSecurity(data);
      setSecurityFailed(false);
    } catch {
      /* Deliberately NOT an error dialog. The other seven tabs are readable and
         a modal over them would make the whole profile look broken; the tab
         states its own failure, where somebody who opened it will read it. */
      setSecurity(null);
      setSecurityFailed(true);
    }
  }, [canReadPlayers, playerId]);

  /* Only when the tab is open: fifty session rows are a cost nobody on the
     other seven tabs is paying for. */
  useEffect(() => {
    if (tab === 'security') void loadSecurity();
  }, [tab, loadSecurity]);

  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadPlayers) {
      setLoading(false);
      return;
    }
    if (!playerId) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    // The handler scopes by the principal's brands and takes no query
    // parameters, so this brandId is advisory — it keeps the console's one
    // spelling for a brand-scoped read and makes the intent legible in the
    // network log. It is a real dependency all the same: changing the global
    // scope must re-read the record, because it decides what the banner below
    // says about which brand this player belongs to.
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    const query = params.toString();

    api
      .get<PlayerDetail>(`/api/players/${encodeURIComponent(playerId)}${query ? `?${query}` : ''}`)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setReadAt(new Date());
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setDetail(null);
        setFailed(true);
        showError({
          title: 'Could not load this player',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'Try again shortly, or reopen the player from the list.',
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canReadPlayers, playerId, brandId, writeCount, showError]);

  const reload = useCallback(() => setWriteCount((n) => n + 1), []);

  const openLift = useCallback((row: ControlRow) => {
    setLiftTarget(row);
    setDialog('lift');
  }, []);

  const submitLift = async (payload: { reasonCode: string; note: string }) => {
    const record = liftTarget?.record;
    if (!record || lifting !== null) return;
    setLifting(record.id);
    try {
      await api.post(`/api/players/restrictions/${record.id}/lift`, payload);
      toast(`${liftTarget?.label ?? 'The control'} lifted — the row keeps both dates.`);
      setDialog(null);
      setLiftTarget(null);
      reload();
    } catch (error: unknown) {
      showError({
        title: 'Could not lift that control',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The control is unchanged. Reopen the profile to re-read it.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setLifting(null);
    }
  };

  const player = detail?.player ?? null;
  const notes = detail?.notes ?? [];
  const restrictions = useMemo(() => detail?.restrictions ?? [], [detail]);
  const bonusGrants = detail?.bonusGrants ?? [];
  /* The two nullable members are the CAPABILITY answer and are passed through
     as null. Defaulting them to [] here would turn "you may not read the
     ledger" into "this account has none", which is the difference the Balances
     tab renders two different sentences for. */
  const balances = detail?.balances ?? null;

  /** The five controls, joined to whatever is on file. Absence means Off. */
  const controls = useMemo<ControlRow[]>(
    () =>
      CONTROLS.map((c) => {
        /* The row IN FORCE, if there is one — an expired row is on file but is
           not the control the operator is looking at, and neither is a lifted
           one. `in_force` is the route's answer, computed with the predicate
           the payout rail uses; the flag on its own would read an expired row
           as On and offer a Lift for a control that is no longer holding
           anything. */
        const record =
          restrictions.find((r) => r.kind === c.kind && r.in_force) ??
          restrictions.find((r) => r.kind === c.kind);
        const limit = record?.limit_amount;
        return {
          kind: c.kind,
          // The stored limit is shown when there is one; the design's
          // "₱50,000 / day" is not, because player_restriction has no period
          // column and inventing "/ day" would misstate a cap an operator
          // reads before releasing money.
          label:
            c.kind === 'DEPOSIT_LIMIT' && limit
              ? `${c.label} ${formatPesos(BigInt(limit))}`
              : c.label,
          description: c.description,
          on: record?.in_force === true,
          record,
          locked:
            record?.in_force === true &&
            record.kind === 'SELF_EXCLUSION' &&
            record.applied_by === 'PLAYER',
        };
      }),
    [restrictions],
  );

  const canRestrict = allowed('players.restrict');
  const controlColumns = useMemo(
    () => restrictionColumns(canRestrict, openLift, lifting),
    [canRestrict, openLift, lifting],
  );

  /* IN FORCE, not merely on file: a lifted row and an expired one are both
     history, and neither is holding this account today. */
  const activeControls = controls.filter((c) => c.on);
  const kycSubmissions = detail?.kycSubmissions ?? [];

  // Every endpoint on this screen sits behind players.view, so a role without
  // it sees the capability it lacks rather than an error dialog it cannot act on.
  if (!canReadPlayers) {
    return (
      <EmptyState icon="lock-key" title="Player profile">
        This module needs the players.view capability. Your role does not hold it.
      </EmptyState>
    );
  }

  if (loading && !player) {
    return (
      <>
        <div className="skeleton" style={{ height: '96px' }}>
          &nbsp;
        </div>
        <div className="skeleton" style={{ height: '78px' }}>
          &nbsp;
        </div>
        <div className="skeleton" style={{ height: '320px' }}>
          &nbsp;
        </div>
      </>
    );
  }

  if (!player) {
    return (
      <EmptyState
        icon="user-circle"
        title={failed ? 'This player did not load' : 'No such player'}
        action={
          <Link className="btn btn-secondary btn-sm" to="/players">
            Back to player management
          </Link>
        }
      >
        {failed
          ? 'The read failed and nothing is shown rather than a partial record. The dialog carries the reason and the request id to quote to support.'
          : `No player with id ${playerId} is visible in your brand scope. A profile outside your scope answers the same way as one that does not exist, so this is not proof the account is gone.`}
      </EmptyState>
    );
  }

  const canReviewKyc = allowed('kyc.review');
  /* Excluding an account from reported revenue has its own key — see the
     permission's own note. Super Admin alone at launch. */
  const canMarkTestAccount = allowed('players.test_account');
  const canAdjust = allowed('funds.adjust');
  const canNote = allowed('players.note');
  const canSetTier = allowed('players.tier');
  /* The resend is the one credential action behind support rather than
     restriction: re-sending a confirmation link is help, not enforcement. */
  const canRespondToSupport = allowed('support.respond');
  const canViewLedger = allowed('transactions.view');
  const role = session?.role ?? 'your role';

  /* Only the moves this account's CURRENT status permits are offered. The
     route refuses the others by name, and a button that exists only to produce
     that refusal is a button that teaches an operator to distrust the screen. */
  const offeredMoves = STATUS_MOVES.filter((m) =>
    (m.from as readonly string[]).includes(player.status),
  );

  const scopedBrand = brands.find((b) => b.id === brandId);
  const outOfScope = Boolean(brandId) && player.brand_id !== brandId;
  const activeTabLabel = TABS.find((t) => t.key === tab)?.label ?? 'Activity';

  /* The design's identity line, with an em-dash wherever the column is null
     rather than a filled-in placeholder. */
  const identityLine = [
    player.ref,
    player.brand_name,
    player.phone ?? 'no phone on file',
    `joined ${formatDay(player.registered_at)}`,
    `agent ${player.agent_ref ?? '—'}`,
  ].join(' · ');

  return (
    <>
      {/* ── Breadcrumb. A real link, so it middle-clicks and opens in a new tab
             the way an operator working two players at once expects. ── */}
      <nav aria-label="Breadcrumb" className="row" style={{ fontSize: '12.5px' }}>
        <Link to="/players" className="text-subtle">
          Player management
        </Link>
        <span className="text-subtle" aria-hidden="true">
          ›
        </span>
        <span className="text-muted">{player.display_name}</span>
      </nav>

      {/* ── The title block ──────────────────────────────────────────────────
             This was a hero band on its own panel: an avatar, the name as an
             <h3>, the three pills, the identity line and the action row, all on
             one surface. It is now the console's one screen header, and three
             things about that are deliberate.

             THE TITLE IS THE PLAYER, NOT THE MODULE. Every other screen titles
             itself with its sidebar name, because that is the thing the operator
             clicked to arrive. This route is not in the sidebar — it is opened
             from a row in Player management, and the breadcrumb directly above
             already says so — so titling it "Player profile" would spend the
             largest text on the page repeating the link just clicked. Whose
             account this is, is the thing somebody working two players at once
             needs to read first.

             THE PURPOSE SITS BETWEEN THE NAME AND THE PILLS. That is the
             header's own layout rather than a choice made here: the sentence
             goes above everything the screen computes, because somebody who
             does not yet know what this page is for cannot read a status pill
             either. The pills are one line lower than they were and no quieter
             for it.

             THE AVATAR SURVIVES, ONE SIZE DOWN. It is decoration — initials,
             aria-hidden — and at the large size it was anchoring a panel that
             no longer exists, so it joins the pills in the meta row, which is
             how QuickLook already draws that same pair in the drawer. Deleting
             it was the easier edit and would have left the two views of one
             player looking like two different products. ── */}
      <ScreenHeader
        title={player.display_name}
        purpose="One player's whole record, opened from Player management — where support and risk staff check an account and decide what to do about it."
        meta={
          <>
            {/* The avatar and the three pills, in one row. The meta slot already
                carries the subtle colour and the 12.5px that the two caption
                divs below used to set by hand, so neither says it twice. */}
            <div className="row">
              <span className="avatar" aria-hidden="true">
                {initials(player.display_name)}
              </span>
              <Tag tone={player.tier.startsWith('VIP') ? 'accent' : 'neutral'}>
                {TIER_LABEL[player.tier] ?? player.tier}
              </Tag>
              <Tag tone={STATUS_TONE[player.status] ?? 'neutral'}>{sentence(player.status)}</Tag>
              <Tag tone={KYC_TONE[player.kyc_status] ?? 'neutral'}>
                {sentence(player.kyc_status)}
              </Tag>
            </div>
            <div>{identityLine}</div>
            {/*
              RESIDENCE, on its own line rather than folded into the identity
              string above.

              It is not another demographic field. PAGCOR's offshore regime
              permits players outside the Philippines and prohibits residents of
              it; the domestic regime is the reverse, and both key on this one
              value — so it decides whether this account was ever allowed to
              exist. It gets its own line for the same reason the KYC tag does.

              NULL prints as words. The account registered before the platform
              asked, nothing has asked since, and there is no route anywhere on
              this platform that sets a player's residence after registration —
              so an operator must not read a blank as "not yet filled in".
            */}
            <div>
              Residence: <ResidenceValue code={player.residence_country} />
              {player.residence_country === null
                ? ' — this account predates the question, and nothing on this platform can set it now'
                : ' — declared at registration, and not editable afterwards'}
            </div>
          </>
        }
        actions={
          <>
            {/* Permission-gated actions are absent, not disabled, for a role that
                does not hold them — a greyed control still tells you the power
                exists. Adjust balance is the one control that stays inert here,
                and the panel at the foot of this page says why. Message is NOT
                among them any more: it opens a support ticket on the player's
                behalf, which is a real write on a real thread they can answer.

                `header-spacer` came off this group with the hand-rolled header:
                inside the header's own action slot there is nothing for a
                margin-left: auto to push against, and a dead class carrying a
                comment about layout is worse than no comment at all. */}
            {canNote && (
              <Button onClick={() => setDialog('note')}>
                <Icon name="plus" />
                Add note
              </Button>
            )}
            {canSetTier && <Button onClick={() => setDialog('tier')}>Set tier</Button>}
            {/* THE LABEL SAYS WHICH WAY IT GOES. "Test account" as a toggle would
                read as a state; this reads as the action it performs. */}
            {canMarkTestAccount && (
              <Button onClick={() => setDialog('test-account')}>
                {player.is_test ? 'Put back into figures' : 'Mark as test account'}
              </Button>
            )}
            {canRestrict &&
              offeredMoves.map((move) => (
                <Button key={move.status} onClick={() => setDialog(move.status)}>
                  {move.label}
                </Button>
              ))}
            {canAdjust && (
              <Button disabled aria-describedby="profile-inert">
                Adjust balance
              </Button>
            )}
            {canRestrict && (
              <Button variant="primary" onClick={() => setDialog('restrict')}>
                Restrict
              </Button>
            )}
            {/*
              * MESSAGE IS A LINK, NOT A NEW ROUTE.
              *
              * This button was disabled on the grounds that "no route addresses
              * a chosen player". That was wrong: `POST /api/support/tickets`
              * raises one on a player's behalf, and a reply to it emails their
              * confirmed address — a path that already exists, is tested, and
              * gives the player a thread they can ANSWER.
              *
              * Building a second operator-to-player email path would have meant
              * two places operator-written copy reaches a player, under two sets
              * of rules, one of which the player cannot reply to. The honest fix
              * was to send this button where the capability lives.
              */}
            <Link className="btn btn-secondary" to={`/support?newTicketFor=${player.id}`}>
              Message
            </Link>
            {/* The one disabled button carries its reason PROGRAMMATICALLY,
                not only in the panel at the foot of the page: a screen reader
                announcing "Adjust balance, dimmed" with nothing attached tells
                somebody the control is broken. The panel repeats it in full. */}
            <span className="sr-only" id="profile-inert">
              {INERT_HERE}
            </span>
          </>
        }
      />

      {outOfScope && (
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            This player belongs to <strong>{player.brand_name}</strong>, which is not the brand the
            console is scoped to
            {scopedBrand ? ` (${scopedBrand.name})` : ''}. Your role can see it, so the record is
            shown — but nothing on this page is filtered by the global scope.
          </div>
        </div>
      )}

      {/* ── Headline figures ─────────────────────────────────────────────────
             All seven read off this record. The three lifetime figures are
             computed by /api/players/:id with the SAME SQL the roster's list
             uses, so this profile and the row it was opened from cannot give
             two answers to one question. ── */}
      <div className="stat-grid">
        <Stat
          label="Cash balance"
          value={formatPesos(BigInt(player.cash_balance))}
          delta={readAt ? `read at ${CLOCK.format(readAt)} · cash only` : 'cash only'}
        />
        <Stat
          label="Lifetime deposits"
          value={formatPesos(BigInt(player.lifetime_deposits))}
          delta="settled deposits only — a failed or pending attempt moved no money"
        />
        <Stat
          label="Lifetime withdrawals"
          value={formatPesos(BigInt(player.lifetime_withdrawals))}
          delta="released only — a request still in the queue has not been paid"
        />
        <Stat
          label="Lifetime GGR"
          value={formatPesos(BigInt(player.lifetime_ggr))}
          delta="bets less wins over every non-voided round"
        />
        <Stat
          label="Risk score"
          value={formatCount(player.risk_score)}
          delta={`${riskBand(player.risk_score)} band · same thresholds as the list`}
        />
        <Stat
          label="Active restrictions"
          value={formatCount(activeControls.length)}
          delta={
            activeControls.length > 0
              ? activeControls.map((c) => c.label.toLowerCase()).join(' · ')
              : restrictions.length > 0
                ? `nothing in force · ${formatCount(restrictions.length)} earlier on file`
                : 'no control on file for this account'
          }
        />
        <Stat
          label="Internal notes"
          value={formatCount(notes.length)}
          delta="staff-only · append-only once saved"
        />
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} label="Player profile sections" />

      <div role="tabpanel" aria-label={`${activeTabLabel} tab`} className="stack">
        {tab === 'activity' && (
          <ActivityTab
            player={player}
            notes={notes}
            canNote={canNote}
            onAddNote={() => setDialog('note')}
            role={role}
          />
        )}

        {tab === 'kyc' && (
          <KycTab
            player={player}
            submissions={kycSubmissions}
            canReview={canReviewKyc}
            role={role}
          />
        )}

        {tab === 'balances' &&
          (balances ? (
            <BalancesTab
              player={player}
              restrictions={restrictions}
              balances={balances}
              bonusGrants={bonusGrants}
              canAdjust={canAdjust}
              role={role}
            />
          ) : (
            /* Unreachable while the route sends `balances` on every profile —
               and rendered rather than skipped anyway, because a tab that shows
               nothing at all is indistinguishable from one whose data is empty.
               A read that FAILED never gets here: the page falls to "This player
               did not load" above it. */
            <Panel title="Balances">
              <EmptyState icon="warning-circle" title="The balance figures did not arrive">
                The profile loaded but carried no balance figures, so nothing here can be stated.
                This is a mismatch between this console and the platform behind it, not a fact about
                the account.
              </EmptyState>
            </Panel>
          ))}

        {tab === 'transactions' && (
          <TransactionsTab
            playerId={player.id}
            playerRef={player.ref}
            canViewLedger={canViewLedger}
            role={role}
          />
        )}

        {tab === 'bonuses' && <BonusesTab grants={bonusGrants} playerRef={player.ref} />}

        {tab === 'points' && <PointsTab playerId={player.id} playerRef={player.ref} />}

        {tab === 'restrictions' && (
          <RestrictionsTab
            controls={controls}
            columns={controlColumns}
            restrictions={restrictions}
            canRestrict={canRestrict}
            onApply={() => setDialog('restrict')}
          />
        )}

        {tab === 'security' && (
          <SecurityTab
            view={security}
            failed={securityFailed}
            canRestrict={canRestrict}
            canResend={canRespondToSupport}
            onSignOut={() => setDialog('sign-out')}
            onForceReset={() => setDialog('force-reset')}
            onResendVerification={() => setDialog('resend-verification')}
            formatMoment={formatManilaMoment}
          />
        )}

        {tab === 'logs' && (
          <LogsTab
            playerId={player.id}
            playerRef={player.ref}
            canExport={allowed('audit.export')}
            formatMoment={formatManilaMoment}
          />
        )}
      </div>

      {/* ── Everything the design shows that this platform cannot source.
             Stated once, in full, so nothing above has to be taken on trust. ── */}
      <Panel title="Not yet available">
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Adjust balance, and only that.</strong> Everything else this profile draws
              now writes — a control, a lift, a status move, a tier, a note and Message — each
              landing its row and its audit entry in one transaction. A balance
              adjustment is the one exception, and not because it is missing: a request against
              manual adjustments IS written and IS decided by a second approver. It is raised on{' '}
              <strong>Player funds</strong>, beside the checker queue that decides it, precisely so
              a one-click balance change from a player page cannot route around maker-checker.
              Putting the form here would put the maker one click from a page that has no checker
              on it.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {/*
                THE TWO PLACES A REASON IS KEPT, for whoever next changes one
                of these forms: `player.player_restriction.reason` and
                `player.player_status_history.reason`. One text column each,
                and each holds the code. (A third, on the older KYC submissions,
                stopped being written when that process closed on 23 October 2026.)

                Those names used to be IN the sentence below, two of them
                set in monospace, in a notice written for a Risk Officer. A
                column name is not something an operator can look up, ask for or
                repeat to anybody — what they can act on is that the dropdown is
                the part that gets stored and the sentence they typed goes
                somewhere else, which is exactly what the rewrite says. The names
                stay here because the claim is only checkable against them.
              */}
              <strong>The reason CODE is what is stored.</strong> A control and a status move each
              keep one reason, and what is kept is the code you chose from the list — not the
              sentence you typed. The code is the only thing a compliance report can group, so
              storing prose in its place would make &ldquo;controls opened by fraud review this
              month&rdquo; uncountable. The reason for a control or a status move is never sent to
              the player.
              The note that came with every decision is on its audit entry, under Reports. An
              internal NOTE is the exception and is stored whole, because it is not a reason for
              something else — it is the record itself.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>&ldquo;Player notified&rdquo;</strong> — only the Security tab&rsquo;s
              password reset and its resent confirmation link email the player, and each says so
              when it is done. <strong>A control, a lift, a status move, a tier and a note do
              not</strong>: none of them records an email and no message is written for them, so
              none of them claims the player was told. A KYC refusal on an older submission emailed
              the player too, until that process closed on 23 October 2026.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {/* No tier-to-benefit, tier-to-limit or tier-to-multiplier table exists. */}
              <strong>A tier moves nothing but the label.</strong> Nothing on this platform links
              a tier to a benefit, a limit or a points multiplier, so
              setting VIP 4 raises no withdrawal ceiling, changes no points multiplier and grants no
              bonus. The programme is run from the label by hand, and the console says so rather
              than implying an effect it cannot produce.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {/*
                WHAT THE PAYOUT PATH ACTUALLY READS, named here rather than on
                screen: the withdrawal release reads `player.player_restriction`
                and the wallet balance, and nothing anywhere reads
                `player.player.status = 'FLAGGED'`.

                The sentence below used to print both of those, the second as a
                fragment of SQL, to the Risk Officer deciding whether a flag was
                enough. The thing they needed was the consequence — a flag holds
                no money, a block does — and a column name was never going to
                help them act on it. The claim is unchanged and still checkable,
                against the two names above.
              */}
              <strong>A flag restrains nothing.</strong> Nothing in this platform treats a flagged
              account as a reason to hold a payout or a deposit — before money is released it is the
              controls in force on the account and the balance behind them that are checked, and a
              flag is neither. Flagging marks the account for the next reviewer; a withdrawal block
              is what holds the money.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {/* It said "nothing on this platform retrieves a document", which stopped being
                  true when the KYC tab began opening the photographs of an identity check
                  (staging, 15 Oct). What is still true is about the older submissions, which
                  are history since that process closed (23 Oct 2026). */}
              <strong>KYC documents</strong> — an older submission is listed on the KYC tab for
              the record, but the documents it named were never uploaded, so there is nothing of
              them to open. An identity check is different: the KYC tab shows how it was decided
              and opens its photographs, for a role holding kyc.review.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Watch</strong> — there is no Watch pill beside Flagged and Restricted.
              A player&rsquo;s status can only be Active, Flagged, Restricted or Suspended, so
              somebody on an informal watch reads here as whatever their stored status is.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Deposit limit period</strong> — a restriction stores the amount but not the
              period it applies over, so a stored cap is shown as an amount and never as
              &ldquo;per day&rdquo;. The period lives with the player&rsquo;s own
              responsible-gaming limits, which this screen cannot read.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {/* The design's URL was players/<id>/<tab>; the console registers /players/:id and
                  carries the tab in ?tab=. */}
              <strong>Links to a tab</strong> — the tab you have open is part of the page&rsquo;s
              address, so a link still opens the same tab after a refresh or when pasted into a
              ticket.
            </div>
          </div>
        </div>
      </Panel>

      {/* ── The decisions ───────────────────────────────────────────────────
             Each dialog owns its own form state and stays mounted while closed,
             because <Dialog> unmounts its children — a half-typed note must
             survive a mis-clicked Cancel. ── */}
      <RestrictDialog
        open={dialog === 'restrict'}
        onClose={() => setDialog(null)}
        playerIds={[player.id]}
        subject={player.display_name}
        onDone={reload}
      />

      <TierDialog
        open={dialog === 'tier'}
        onClose={() => setDialog(null)}
        playerIds={[player.id]}
        currentTier={player.tier}
        subject={player.display_name}
        onDone={reload}
      />

      {/*
        The three credential actions. One component, keyed by which dialog is
        open — they ask the same two questions and differ only in vocabulary,
        consequence copy and whether the note is required.

        `onDone` re-reads the sessions rather than the whole profile: the
        counts and the list are what moved, and the record above them did not.
      */}
      {(['sign-out', 'force-reset', 'resend-verification'] as const).map((key) => {
        const action: CredentialAction =
          key === 'sign-out'
            ? 'SIGN_OUT'
            : key === 'force-reset'
              ? 'FORCE_RESET'
              : 'RESEND_VERIFICATION';
        return (
          <CredentialActionDialog
            key={key}
            action={action}
            open={dialog === key}
            onClose={() => setDialog(null)}
            playerId={player.id}
            subject={player.display_name}
            liveSessions={security?.liveCount ?? 0}
            standing={security ? signInStanding(security.player) : null}
            onDone={() => void loadSecurity()}
          />
        );
      })}

      <TestAccountDialog
        open={dialog === 'test-account'}
        onClose={() => setDialog(null)}
        playerId={player.id}
        subject={player.display_name}
        isTest={player.is_test}
        onDone={reload}
      />

      <NoteDialog
        open={dialog === 'note'}
        onClose={() => setDialog(null)}
        playerId={player.id}
        subject={player.display_name}
        onDone={reload}
      />

      {STATUS_MOVES.map((move) => (
        <StatusDialog
          key={move.status}
          open={dialog === move.status}
          onClose={() => setDialog(null)}
          target={move.status}
          playerIds={[player.id]}
          subject={player.display_name}
          onDone={reload}
        />
      ))}

      <LiftDialog
        open={dialog === 'lift'}
        onClose={() => {
          setDialog(null);
          setLiftTarget(null);
        }}
        target={liftTarget}
        busy={lifting !== null}
        onSubmit={submitLift}
      />
    </>
  );
}
